import type { MeterDetail } from '@rheora/enrollment-contract';

import { useTranslation } from '@fluxora/i18n';
import { Dialog } from '@fluxora/ui';

import { meterDetailFields } from '../services/meterDetail.service';
import { MeterFieldList } from './MeterFieldList';

/**
 * "See all meter details" dialog (SHOP-3 / SHOP-4). Opened from the
 * `MeterSummaryCard` IconButton; lists the FULL meter field set (research §2) —
 * the resolved meter's identity/territory (ESI ID display-only, premise, TDU,
 * load zone), meter hardware (smart/legacy), usage character (customer type,
 * estimated annual usage), and enrollment routing (service status, switch/move-in).
 *
 * Reused by BOTH meter-first on-ramps (ESI ID typed directly, or resolved from
 * ZIP); the fields come from the pure `meterDetailFields` service, so this
 * component only renders. `@fluxora/ui` `Dialog` owns the modal a11y.
 */
export interface MeterDetailsDialogProps {
  readonly open: boolean;
  readonly onClose: () => void;
  /** The resolved meter whose details are shown. */
  readonly meterDetail: MeterDetail;
}

export function MeterDetailsDialog({
  open,
  onClose,
  meterDetail,
}: MeterDetailsDialogProps): JSX.Element {
  const { t } = useTranslation();
  const fields = meterDetailFields(meterDetail);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      size="lg"
      title={t('shop.meter.detail.title')}
      description={t('shop.meter.detail.subtitle')}
    >
      <MeterFieldList fields={fields} columns={2} />
    </Dialog>
  );
}
